/**
 * Voice AI — Agent Pipeline Tab
 * Provider selection (LLM/STT/TTS) and per-agent pipeline overrides.
 * Uses reactive agent-changed events for override toggling,
 * and a save event for persisting.
 * Dispatches: save, agent-changed
 */

import { LitElement, html } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { agentStyles } from '../../styles/agent-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';

const DEFAULTS = {
  pipeline_mode: 'standard',
  realtime_voice: 'coral',
  stt_endpointing: 600,
  stt_utterance_end_ms: 1500,
  bargein_mode: 'allow',
  bargein_min_words: 2,
  tts_speed: 1.0,
  llm_temperature: 0.7,
  llm_max_tokens: 300,
  max_history: 40,
  sentence_min_length: 10,
};

export class AgentPipelineTab extends LitElement {
  static properties = {
    agent: { type: Object },
    saving: { type: Boolean },
    llmProviders: { type: Array },
    sttProviders: { type: Array },
    ttsProviders: { type: Array },
    contextualLlmProviders: { type: Array },
  };

  static styles = [designSystem, componentStyles, agentStyles];

  constructor() {
    super();
    this.llmProviders = [];
    this.sttProviders = [];
    this.ttsProviders = [];
    this.contextualLlmProviders = [];
  }

  /* ─── Pipeline Override Helpers ─── */

  _getOverride(key) {
    return this.agent?.pipelineOverrides?.[key] ?? null;
  }

  _hasOverride(key) {
    const val = this.agent?.pipelineOverrides?.[key];
    return val !== undefined && val !== null && val !== '';
  }

  _toggleOverride(key) {
    const agent = { ...this.agent };
    const overrides = { ...(agent.pipelineOverrides || {}) };
    if (this._hasOverride(key)) {
      delete overrides[key];
    } else {
      overrides[key] = DEFAULTS[key];
    }
    agent.pipelineOverrides = Object.keys(overrides).length > 0 ? overrides : {};
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent } }));
  }

  _setOverride(key, value) {
    const agent = { ...this.agent };
    const overrides = { ...(agent.pipelineOverrides || {}) };
    overrides[key] = value;
    agent.pipelineOverrides = overrides;
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent } }));
  }

  _setProviderKey(field, value) {
    const agent = { ...this.agent };
    agent[field] = value || null;
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent } }));
  }

  _save() {
    this.dispatchEvent(new CustomEvent('save', { detail: { agent: this.agent } }));
  }

  /* ─── Render Helpers ─── */

  _renderProviderSelect(field, label, icon, iconColor, providers, hint) {
    const currentKey = this.agent[field] || '';
    return html`
      <div class="form-group">
        <label class="form-label">
          <i class="fa-sharp fa-light ${icon}" style="margin-right:var(--space-1);color:${iconColor};"></i>
          ${label}
        </label>
        <select class="form-select"
          .value=${currentKey}
          @change=${e => this._setProviderKey(field, e.target.value)}
        >
          <option value="" ?selected=${!currentKey}>Use Global Default</option>
          ${providers.map(p => html`
            <option value="${p.key}" ?selected=${currentKey === p.key}>
              ${p.label || p.key} (${p.provider}/${p.model})
            </option>
          `)}
        </select>
        <div class="form-hint">${hint}</div>
      </div>
    `;
  }

  _renderOverrideField(key, label, hint, type, opts = {}) {
    const hasOverride = this._hasOverride(key);
    const currentVal = hasOverride ? this._getOverride(key) : DEFAULTS[key];

    return html`
      <div class="form-group" style="padding:var(--space-3) 0;border-bottom:1px solid var(--color-border-subtle);">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:${hasOverride ? 'var(--space-2)' : '0'};">
          <div>
            <label class="form-label" style="margin-bottom:0;">${label}</label>
            <div class="form-hint" style="margin-top:2px;">${hint}</div>
          </div>
          <div style="display:flex;align-items:center;gap:var(--space-2);">
            <span style="font-size:var(--text-xs);color:var(--color-text-tertiary);">
              ${hasOverride ? 'Custom' : 'Global Default'}
            </span>
            <div
              class="toggle ${hasOverride ? 'active' : ''}"
              @click=${() => this._toggleOverride(key)}
            ></div>
          </div>
        </div>
        ${hasOverride ? html`
          <div style="margin-top:var(--space-2);">
            ${type === 'slider' ? this._renderSlider(key, currentVal, opts) : ''}
            ${type === 'select' ? this._renderSelect(key, currentVal, opts) : ''}
            ${type === 'number' ? this._renderNumber(key, currentVal, opts) : ''}
            ${type === 'max_tokens' ? this._renderMaxTokens(key, currentVal) : ''}
          </div>
        ` : ''}
      </div>
    `;
  }

  _renderSlider(key, currentVal, opts) {
    return html`
      <div style="display:flex;align-items:center;gap:var(--space-3);">
        <input type="range"
          min="${opts.min}" max="${opts.max}" step="${opts.step || 1}"
          .value=${String(currentVal)}
          @input=${e => this._setOverride(key, opts.float ? parseFloat(e.target.value) : parseInt(e.target.value))}
          style="flex:1;"
        />
        <span class="slider-value" style="min-width:52px;text-align:center;">
          ${opts.float ? Number(currentVal).toFixed(1) : currentVal}${opts.suffix || ''}
        </span>
      </div>
    `;
  }

  _renderSelect(key, currentVal, opts) {
    return html`
      <select class="form-select" style="max-width:300px;"
        .value=${currentVal}
        @change=${e => this._setOverride(key, e.target.value)}
      >
        ${opts.options.map(o => html`<option value="${o.value}" ?selected=${currentVal === o.value}>${o.label}</option>`)}
      </select>
    `;
  }

  _renderMaxTokens(key, currentVal) {
    const noMax = currentVal === 0;
    return html`
      <div style="display:flex;align-items:center;gap:var(--space-2);margin-bottom:var(--space-2);">
        <div
          class="toggle ${noMax ? 'active' : ''}"
          @click=${() => this._setOverride(key, noMax ? 300 : 0)}
        ></div>
        <span style="font-size:var(--text-sm);color:var(--color-text-secondary);">No Max</span>
      </div>
      ${!noMax ? html`
        <div style="display:flex;align-items:center;gap:var(--space-3);">
          <input type="range" min="100" max="10000" step="100"
            .value=${String(currentVal)}
            @input=${e => this._setOverride(key, parseInt(e.target.value))}
            style="flex:1;"
          />
          <span class="slider-value" style="min-width:52px;text-align:center;">
            ${currentVal.toLocaleString()}
          </span>
        </div>
      ` : html`
        <span style="font-size:var(--text-xs);color:var(--color-primary);">Provider default — no cap on response length.</span>
      `}
    `;
  }

  _renderNumber(key, currentVal, opts) {
    return html`
      <input type="number" class="form-input" style="width:100px;font-family:var(--font-mono);text-align:center;"
        min="${opts.min}" max="${opts.max}" step="${opts.step || 1}"
        .value=${String(currentVal)}
        @change=${e => this._setOverride(key, Math.max(opts.min, Math.min(opts.max, parseInt(e.target.value) || DEFAULTS[key])))}
      />
    `;
  }

  _mergeProviders(primary, secondary) {
    const seen = new Set();
    const merged = [];
    for (const p of [...(primary || []), ...(secondary || [])]) {
      if (!seen.has(p.key)) {
        seen.add(p.key);
        merged.push(p);
      }
    }
    return merged;
  }

  /* ─── Main Render ─── */

  render() {
    const agent = this.agent;
    if (!agent) return html``;

    return html`
      ${iconStyles}
      <!-- Provider Selection -->
      <div class="config-section">
        <div class="config-section-title">
          <i class="fa-sharp fa-light fa-microchip-ai" style="margin-right:var(--space-2);color:var(--color-info);"></i>
          Provider Selection
        </div>
        <div class="config-section-desc">
          Choose which LLM, STT, and TTS providers this agent uses. Select "Use Global Default" to inherit from the system-wide settings.
        </div>

        <div class="form-grid" style="margin-top:var(--space-3);">
          ${this._renderProviderSelect('llmProviderKey', 'LLM Provider', 'fa-brain', 'var(--color-accent)', this.llmProviders,
            'The language model used for generating responses during calls.')}
          ${this._renderProviderSelect('sttProviderKey', 'STT Provider', 'fa-waveform-lines', 'var(--color-primary)', this.sttProviders,
            'The speech-to-text engine used to transcribe caller audio.')}
        </div>
        <div class="form-grid" style="margin-top:var(--space-4);">
          ${this._renderProviderSelect('ttsProviderKey', 'TTS Provider', 'fa-volume-high', 'var(--color-warning)', this.ttsProviders,
            'The text-to-speech engine used to generate the agent voice.')}
        </div>
        <div class="form-grid" style="margin-top:var(--space-4);">
          ${this._renderProviderSelect('contextualLlmProviderKey', 'Contextual LLM', 'fa-bolt', 'var(--color-success)',
            this._mergeProviders(this.contextualLlmProviders, this.llmProviders),
            'Fast model for instant contextual acknowledgments. Overrides the global contextual LLM setting.')}
        </div>
      </div>

      <!-- Pipeline Overrides -->
      <div class="config-section">
        <div class="config-section-title">
          <i class="fa-sharp fa-light fa-diagram-project" style="margin-right:var(--space-2);color:var(--color-accent);"></i>
          Pipeline Overrides
        </div>
        <div class="config-section-desc">
          Override global pipeline defaults for this agent. Toggle "Custom" to set agent-specific values. When off, the global default is used.
        </div>

        ${this._renderOverrideField('pipeline_mode', 'Pipeline Mode', 'Override the global pipeline for this agent', 'select', {
          options: [
            { value: 'standard', label: 'Standard (STT + LLM + TTS)' },
            { value: 'realtime', label: 'Realtime (OpenAI end-to-end)' },
          ]
        })}
        ${this._hasOverride('pipeline_mode') && this._getOverride('pipeline_mode') === 'realtime' ? html`
          ${this._renderOverrideField('realtime_voice', 'Realtime Voice', 'OpenAI voice for this agent in realtime mode', 'select', {
            options: [
              { value: 'alloy', label: 'Alloy' }, { value: 'ash', label: 'Ash' },
              { value: 'ballad', label: 'Ballad' }, { value: 'coral', label: 'Coral' },
              { value: 'echo', label: 'Echo' }, { value: 'sage', label: 'Sage' },
              { value: 'shimmer', label: 'Shimmer' }, { value: 'verse', label: 'Verse' },
              { value: 'marin', label: 'Marin' }, { value: 'cedar', label: 'Cedar' },
            ]
          })}
        ` : ''}

        ${this._renderOverrideField('stt_endpointing', 'Endpointing', 'Silence before speech segment is final', 'slider', { min: 200, max: 1500, step: 50, suffix: 'ms' })}
        ${this._renderOverrideField('stt_utterance_end_ms', 'Utterance End Timeout', 'Silence before full utterance is sent to AI', 'slider', { min: 500, max: 5000, step: 100, suffix: 'ms' })}
        ${this._renderOverrideField('bargein_mode', 'Barge-in Mode', 'How caller interruptions are handled', 'select', {
          options: [
            { value: 'allow', label: 'Allow — Stop and listen' },
            { value: 'mute', label: 'Mute — Ignore while speaking' },
            { value: 'disabled', label: 'Disabled — Never interrupt' },
          ]
        })}
        ${this._renderOverrideField('bargein_min_words', 'Min Words to Interrupt', 'Minimum words before barge-in triggers', 'number', { min: 1, max: 10 })}
        ${this._renderOverrideField('tts_speed', 'TTS Speed', 'Speech synthesis speed multiplier', 'slider', { min: 0.5, max: 2.0, step: 0.1, float: true, suffix: 'x' })}
        ${this._renderOverrideField('llm_temperature', 'LLM Temperature', 'AI creativity/randomness', 'slider', { min: 0.0, max: 1.5, step: 0.1, float: true })}
        ${this._renderOverrideField('llm_max_tokens', 'Max Response Tokens', 'Maximum AI response length', 'max_tokens', {})}
        ${this._renderOverrideField('max_history', 'Conversation Memory', 'Messages kept in context', 'number', { min: 10, max: 100 })}
        ${this._renderOverrideField('sentence_min_length', 'Min Sentence Length', 'Min chars before sending to TTS', 'number', { min: 0, max: 50 })}
      </div>

      <div class="actions-footer" style="justify-content:flex-end;">
        <button class="btn btn-primary" ?disabled=${this.saving} @click=${() => this._save()}>
          <i class="fa-sharp fa-light ${this.saving ? 'fa-spinner-third fa-spin' : 'fa-floppy-disk'}"></i>
          ${this.saving ? 'Saving...' : 'Save Pipeline Settings'}
        </button>
      </div>
    `;
  }
}

customElements.define('agent-pipeline-tab', AgentPipelineTab);
